import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useRelationEntryScreen } from './hooks/useRelationEntryScreen';
import { appToast } from '../../../components/AppToast';

function TextField({ label, value, onChange, placeholder, required = false, type = 'text' }) {
  return (
    <label className="grid gap-2">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
        {label}
        {required ? <span className="text-error"> *</span> : null}
      </span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border-0 bg-surface-container-low px-4 py-3.5 text-base text-on-surface outline-none placeholder:text-on-surface-variant transition-all focus:ring-2 focus:ring-primary/20"
      />
    </label>
  );
}

export default function RelationEntryScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const { state, actions } = useRelationEntryScreen(id !== 'new' ? id : null);

  useEffect(() => {
    if (state.error) appToast.error(state.error);
  }, [state.error]);

  return (
    <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-6 pb-8">
      <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Masters</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Relation Master</span>
          </div>
          <h1 className="mt-2 text-4xl font-extrabold tracking-[-0.02em] text-on-surface">
            {state.isEditMode ? 'Edit Relation' : 'Create Relation'}
          </h1>
          <p className="mt-2 max-w-xl text-base text-on-surface-variant">
            {state.isEditMode ? 'Modify existing relation details' : 'Create a new relation record'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/masters/relation/list"
            className="inline-flex items-center justify-center rounded-xl bg-secondary-container px-7 py-3 text-sm font-bold text-secondary"
          >
            Discard
          </Link>
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving}
            className="inline-flex items-center justify-center rounded-xl bg-primary px-8 py-3 text-sm font-bold text-on-primary shadow-lg shadow-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {state.isSaving ? 'Saving...' : 'Save Relation'}
          </button>
        </div>
      </section>

      {state.isLoading ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-14 text-center text-sm text-on-surface-variant">
          Loading relation details...
        </div>
      ) : (
        <section className="grid grid-cols-12 gap-6">
          <div className="col-span-12 space-y-6 lg:col-span-8">
            <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-lowest p-8">
              <div className="mb-8 flex items-center gap-4">
                <div className="h-10 w-1.5 rounded-full bg-primary" />
                <h2 className="text-3xl font-bold text-on-surface">General Identification</h2>
              </div>

              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <div className="md:col-span-2">
                  <TextField
                    label="Name"
                    required
                    value={state.form.name}
                    onChange={(val) => actions.patchForm('name', val)}
                    placeholder="e.g. Father, Mother, Guardian"
                  />
                </div>
              </div>
            </article>
          </div>

          <aside className="col-span-12 space-y-6 lg:col-span-4">
            <article className="rounded-[2rem] border border-outline-variant/15 bg-surface-container-low p-6">
              <div className="grid gap-2">
                <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                  Operational Status
                </span>
                <div className="flex items-center rounded-xl bg-surface-container-low p-1">
                  <button
                    type="button"
                    onClick={() => actions.setOperationalStatus('active')}
                    className={`flex-1 rounded-lg px-3 py-2 text-sm font-bold transition-all ${
                      state.operationalStatus === 'active'
                        ? 'bg-surface-container-lowest text-primary shadow-sm'
                        : 'text-on-surface-variant'
                    }`}
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => actions.setOperationalStatus('inactive')}
                    className={`flex-1 rounded-lg px-3 py-2 text-sm font-bold transition-all ${
                      state.operationalStatus === 'inactive'
                        ? 'bg-surface-container-lowest text-on-surface shadow-sm'
                        : 'text-on-surface-variant'
                    }`}
                  >
                    Inactive
                  </button>
                </div>
              </div>
            </article>
          </aside>
        </section>
      )}
    </div>
  );
}
